import React, { useCallback } from "react";
import { useSelector } from "react-redux";
import { usePreorderCommunication } from "@/hooks/usePreorderCommunication";
import { useRestoplaceLogin } from "@/core/session/hooks";
import { selectDebug } from "@/core/app/selectors";
import type { ThemeChangeData } from "@/types/iframe-messages";

interface AppInitializerProps {
  children: React.ReactNode;
}

// Stable constants outside component to prevent recreation on each render
const DEFAULT_CAPABILITIES = ["ordering", "theming", "closing", "navigation"];

// Get trusted origins from environment variables (computed once)
const TRUSTED_ORIGINS: string[] = (() => {
  const envOrigins = import.meta.env.VITE_TRUSTED_ORIGINS;
  if (envOrigins) {
    return envOrigins.split(",").map((origin: string) => origin.trim());
  }
  return ["*"];
})();

/**
 * AppInitializer component that handles iframe communication
 * and app initialization logic
 */
export const AppInitializer: React.FC<AppInitializerProps> = ({ children }) => {
  // Read debug flag from Redux (set by DataLoader from URL param debug=true)
  const enableLogging = useSelector(selectDebug);

  // Stable callback for theme change - memoized to prevent infinite loops
  const handleThemeChange = useCallback((themeData: ThemeChangeData) => {
    // Theme is already handled in usePreorderCommunication via Redux
    // This callback is for additional custom logic if needed

    // Log theme application for debugging
    if (process.env.NODE_ENV === "development") {
      console.log("Theme change received from parent:", themeData);
      console.log("Theme applied to MUI via Redux:", {
        theme: themeData.theme,
        hasColors: !!themeData.colors,
        hasCustomTheme: themeData.theme === "custom",
      });
    }
  }, []);

  // Stable callback for parent ready - memoized
  const handleParentReady = useCallback(() => {
    console.log("Parent window ready for communication");
    // Additional initialization logic can be added here
  }, []);

  const restoplaceLogin = useRestoplaceLogin();

  // Код авторизации Restoplace — одноразовый credential: в консоль он не
  // попадает ни в каком режиме.
  const handleAuthCodeReceived = useCallback(
    (code: string) => {
      const trimmed = code.trim();
      if (!trimmed) return;
      restoplaceLogin.mutate({ body: { code: trimmed } });
    },
    [restoplaceLogin]
  );

  // Initialize iframe communication with stable references
  // Stage 5: Using usePreorderCommunication based on iframe-widget-core
  usePreorderCommunication({
    autoDetectIframe: true,
    autoSendReady: true,
    enableLogging,
    defaultSource: "delivery",
    capabilities: DEFAULT_CAPABILITIES,
    onThemeChange: handleThemeChange,
    onParentReady: handleParentReady,
    onAuthCodeReceived: handleAuthCodeReceived,
    trustedOrigins: TRUSTED_ORIGINS,
  });

  return <>{children}</>;
};
